import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { Chip } from '../../shared/ui/chip';
import { FilterBar } from '../../shared/ui/filter-bar';
import { SearchIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import { Segmented } from '../../shared/ui/segmented';
import {
  FacetPanel,
  FacetValue,
  PeriodPicker,
  facetValueText,
  type IFacetBucket,
  type IFacetSource,
} from '../event-filters';
import type { TFacetField } from '../../entities/event';
import {
  RUN_FACET_GROUPS,
  RUN_FACET_LABEL,
  RUN_FACET_WORD,
  RUN_SHOW,
  runFacetValues,
  runFacets,
  runKeys,
  withRunFacet,
  type IRunFilters,
  type TFacetCount,
  type TRunCountedField,
  type TRunQuery,
  type TRunShow,
} from '../../entities/run';

const SHOW_STATUSES: Record<Exclude<TRunShow, 'all'>, readonly string[]> = {
  problems: ['failed', 'error', 'failing'],
  progress: ['running'],
  stopped: ['filtered', 'duplicate', 'stopped'],
};

// The runs API refuses a window wider than 31 days.
const MAX_MINUTES = 30 * 24 * 60;

// Same field, same values, same words as the shared facet renderer.
const SHARED: Partial<Record<TRunCountedField, TFacetField>> = {
  sourceId: 'sourceId',
  destinationId: 'destinationId',
  customerId: 'customerId',
  eventType: 'eventType',
  responseStatus: 'responseStatus',
  responseStatusClass: 'responseStatusClass',
  errorClass: 'errorClass',
  attempts: 'attempts',
  authResult: 'authResult',
};

const NAMED: readonly TRunCountedField[] = [
  'flowId',
  'sourceId',
  'destinationId',
  'customerId',
  'nodeKey',
];

const valueText = (field: TRunCountedField, bucket: IFacetBucket): string => {
  const shared = SHARED[field];
  if (shared) return facetValueText(shared, bucket);
  const value = bucket.value ?? '';
  return RUN_FACET_WORD[field]?.[value] ?? bucket.label ?? value;
};

// An id copied from the list jumps to its run; anything else is the search language.
const ID = /^(run|req|evt)_[A-Za-z0-9]+$/;

interface IRunFilterBarProps {
  filters: IRunFilters;
  onChange: (next: IRunFilters) => void;
  /** The list's query: facet counts are asked over exactly what the list shows. */
  query: TRunQuery;
  statusCounts: readonly TFacetCount[] | undefined;
}

export const RunFilterBar = ({ filters, onChange, query, statusCounts }: IRunFilterBarProps) => {
  const typed = filters.q ?? filters.search ?? '';
  const [draft, setDraft] = useState(typed);
  useEffect(() => setDraft(typed), [typed]);

  const submit = (text: string) => {
    const trimmed = text.trim();
    if (trimmed === typed) return;
    onChange(
      ID.test(trimmed)
        ? { ...filters, q: trimmed, search: undefined }
        : { ...filters, q: undefined, search: trimmed || undefined },
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => submit(draft), 600);
    return () => clearTimeout(timer);
  });

  const statusCount = (show: TRunShow): number | undefined => {
    if (!statusCounts) return undefined;
    return (
      statusCounts
        // `failing` is a subset of `running`: counted in Problems, never twice in All.
        .filter((facet) =>
          show === 'all' ? facet.value !== 'failing' : SHOW_STATUSES[show].includes(facet.value),
        )
        .reduce((sum, facet) => sum + facet.count, 0)
    );
  };

  const active = RUN_FACET_GROUPS.flatMap((group) => group.facets)
    .map((facet) => facet.field)
    .filter((field) => runFacetValues(filters, field).length > 0);

  // Names over the window alone: the other filters can count a picked value away.
  const namedFields = active.filter((field) => NAMED.includes(field));
  const window = { from: query.from, to: query.to };
  const names = useQuery({
    queryKey: runKeys.facets({ ...window, fields: namedFields }),
    queryFn: () => runFacets({ ...window, fields: namedFields }),
    enabled: namedFields.length > 0,
  });
  const nameOf = (field: TRunCountedField, value: string): IFacetBucket => {
    return names.data?.[field]?.find((each) => each.value === value) ?? { value, count: 0 };
  };

  const source = useMemo<IFacetSource<TRunCountedField>>(
    () => ({
      groups: RUN_FACET_GROUPS,
      label: (field) => RUN_FACET_LABEL[field],
      selected: (field) => runFacetValues(filters, field),
      count: (field) => ({
        queryKey: runKeys.facets({ ...query, fields: [field] }),
        queryFn: () =>
          runFacets({ ...query, fields: [field] }).then((answer) => answer[field] ?? []),
      }),
      text: valueText,
      render: (field, bucket) => {
        const shared = SHARED[field];
        if (shared) return <FacetValue field={shared} bucket={bucket} />;
        return <span className="min-w-0 truncate">{valueText(field, bucket)}</span>;
      },
      // A customer in the body is not a customer until the source says where it is.
      emptyNote: (field) =>
        field === 'customerId' ? (
          <>
            No request in this window named a customer. A source reads it from the field you set
            under Customer on{' '}
            {filters.sourceId.length === 1 ? (
              <Link
                to="/sources/$sourceId"
                params={{ sourceId: filters.sourceId[0] }}
                hash="customer"
                className="text-primary underline"
              >
                its page
              </Link>
            ) : (
              <Link to="/sources" className="text-primary underline">
                the source’s page
              </Link>
            )}
            .
          </>
        ) : undefined,
    }),
    [filters, query],
  );

  const set = (field: TRunCountedField, values: string[]) =>
    onChange(withRunFacet(filters, field, values));

  return (
    <FilterBar className="flex-wrap">
      <PeriodPicker
        filters={filters}
        maxMinutes={MAX_MINUTES}
        onChange={(next) => onChange({ ...next, minutes: next.minutes ?? filters.minutes })}
      />

      <Segmented
        aria-label="Which runs"
        value={filters.show}
        onChange={(show) => onChange({ ...filters, show })}
        segments={RUN_SHOW.map((option) => ({
          value: option.value,
          label: option.label,
          count: option.value === 'all' ? undefined : statusCount(option.value),
        }))}
      />

      <Chip
        active={filters.agent === true}
        aria-pressed={filters.agent === true}
        title="Only runs with an Agent step"
        onClick={() =>
          onChange({
            ...filters,
            agent: filters.agent ? undefined : true,
            agentEnd: filters.agent ? undefined : filters.agentEnd,
          })
        }
      >
        Agents
      </Chip>
      <Chip
        active={filters.agentEnd === 'limit'}
        aria-pressed={filters.agentEnd === 'limit'}
        title="Runs whose agent ran out of steps, money or time"
        onClick={() =>
          onChange({
            ...filters,
            agentEnd: filters.agentEnd ? undefined : 'limit',
            agent: filters.agentEnd ? filters.agent : true,
          })
        }
      >
        Hit a limit
      </Chip>

      {filters.issueId.length > 0 && (
        <Chip active onClear={() => onChange({ ...filters, issueId: [] })}>
          On one issue
        </Chip>
      )}

      {active.map((field) => {
        const values = runFacetValues(filters, field);
        const words = values.map((value) => valueText(field, nameOf(field, value)));
        const all = `${RUN_FACET_LABEL[field]}: ${words.join(', ')}`;
        return (
          <FacetPanel
            key={field}
            field={field}
            source={source}
            onChange={set}
            trigger={
              <Chip active title={all} onClear={() => set(field, [])}>
                <span className="max-w-chip truncate">
                  {values.length > 1 ? `${RUN_FACET_LABEL[field]}: ${values.length} selected` : all}
                </span>
              </Chip>
            }
          />
        );
      })}

      <FacetPanel source={source} onChange={set} />

      <form
        className="ml-auto"
        onSubmit={(event) => {
          event.preventDefault();
          submit(draft);
        }}
      >
        <Input
          size="row"
          type="search"
          leading={<SearchIcon size={13} />}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Search runs"
          aria-label="Search runs"
          title="An id (run_, req_, evt_) jumps to its run. Otherwise the events search language: code:5xx, type:order.paid, data.object.status:past_due, or a bare word to look for in the body."
          className="w-search"
        />
      </form>
    </FilterBar>
  );
};
